import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  Input,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { IonicModule } from '@ionic/angular';
import {
  Chart,
  DoughnutController,
  ArcElement,
  Title,
  Legend,
  Tooltip,
  Plugin,
  ChartConfiguration
} from 'chart.js';
import { AnalyzeNettleie } from 'src/app/services/analyse/analyse.types';

@Component({
  selector: 'app-your-hours',
  standalone: true,
  imports: [CommonModule, IonicModule],
  template: `
    <ion-row>
      <ion-col class="mx-3">
        <p class="text-lg"><strong>Dine timer</strong></p>
        <p>
          Her er en oversikt over alle dine forbrukstimer for måneden. Her kan
          du se hvor mange timer du har i inneværende trinn, trinnet over og
          trinnet under.
        </p>

        <div class="flex justify-between gap-4 text-center">
          @if (!isInLowestStep){
          <div class="flex flex-col flex-1 items-center">
            <p>Under</p>
            <div class="w-full max-w-64">
              <canvas #chart_below id="hours-below"></canvas>
            </div>
            <p class="mb-0">{{ analyzeNettleie.num_below }} timer</p>
            <p class="m-0">
              <strong>{{ belowPercent.toFixed(0) }}%</strong>
            </p>
          </div>
          }
          <div class="flex flex-col flex-1 items-center">
            <p>Inneværende</p>
            <div class="w-full max-w-64">
              <canvas #chart_current id="hours-current"></canvas>
            </div>
            <p class="mb-0">{{ analyzeNettleie.num_current }} timer</p>
            <p class="m-0">
              <strong>{{ currentPercent.toFixed(0) }}%</strong>
            </p>
          </div>
          @if (!isInHighestStep){
          <div class="flex flex-col flex-1 items-center">
            <p>Over</p>
            <div class="w-full max-w-64">
              <canvas #chart_above id="hours-above"></canvas>
            </div>
            <p class="mb-0">{{ analyzeNettleie.num_above }} timer</p>
            <p class="m-0">
              <strong>{{ abovePercent.toFixed(0) }}%</strong>
            </p>
          </div>
          }
        </div>

        @if (analyzeNettleie.num_current < 16 && analyzeNettleie.num_below >
        analyzeNettleie.num_current) {
        <p class="p-4 bg-[--primary-strong-100] rounded-lg">
          <strong>Tips!</strong>
          Vi ser at det er svært få timer i måneden som tipper deg over i et
          høyere trinn. Hvis du har mulighet til å fordele forbruket ditt
          jevnere utover og unngå de høyeste forbrukstoppene, kan du spare
          penger.
        </p>
        }
      </ion-col>
    </ion-row>
  `
})
export class YourHoursComponent {
  @Input() analyzeNettleie: AnalyzeNettleie;
  @Input() errorAnalyse: boolean;

  @ViewChild('chart_above') chartRefA: ElementRef<HTMLCanvasElement>;
  @ViewChild('chart_below') chartRefB: ElementRef<HTMLCanvasElement>;
  @ViewChild('chart_current') chartRefC: ElementRef<HTMLCanvasElement>;

  ctxA: HTMLCanvasElement;
  ctxB: HTMLCanvasElement;
  ctxC: HTMLCanvasElement;

  chartA: Chart<'doughnut'>;
  chartB: Chart<'doughnut'>;
  chartC: Chart<'doughnut'>;

  isInLowestStep: boolean;
  isInHighestStep: boolean;
  totalHours: number;
  belowPercent: number;
  currentPercent: number;
  abovePercent: number;

  constructor() {
    Chart.register(DoughnutController, ArcElement, Title, Legend, Tooltip);
  }

  ngAfterViewInit() {
    this.ctxC = this.chartRefC.nativeElement;
    this.createChart(this.ctxC, 'C');
    if (!this.isInLowestStep) {
      this.ctxB = this.chartRefB.nativeElement;
      this.createChart(this.ctxB, 'B');
    }
    if (!this.isInHighestStep) {
      this.ctxA = this.chartRefA.nativeElement;
      this.createChart(this.ctxA, 'A');
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.analyzeNettleie) {
      this.isInLowestStep = this.analyzeNettleie.current_step.step === 1;
      this.isInHighestStep =
        this.analyzeNettleie.current_step.step ===
        this.analyzeNettleie.steps.length;
      if (
        !changes.analyzeNettleie?.firstChange &&
        changes.analyzeNettleie?.previousValue.current_step.step === 1 &&
        changes.analyzeNettleie?.currentValue.current_step.step !== 1
      ) {
        this.ctxB = this.chartRefB.nativeElement;
        this.createChart(this.ctxB, 'B');
      }
      if (
        !changes.analyzeNettleie?.firstChange &&
        changes.analyzeNettleie?.previousValue.current_step.step ===
          this.analyzeNettleie.steps.length &&
        changes.analyzeNettleie?.currentValue.current_step.step !==
          this.analyzeNettleie.steps.length
      ) {
        this.ctxA = this.chartRefA.nativeElement;
        this.createChart(this.ctxA, 'A');
      }
      this.totalHours =
        this.analyzeNettleie.num_below +
        this.analyzeNettleie.num_current +
        this.analyzeNettleie.num_above;

      this.belowPercent =
        (this.analyzeNettleie.num_below / this.totalHours) * 100;
      this.currentPercent =
        (this.analyzeNettleie.num_current / this.totalHours) * 100;
      this.abovePercent =
        (this.analyzeNettleie.num_above / this.totalHours) * 100;

      if (!changes.analyzeNettleie?.firstChange) {
        this.createChart(this.ctxA, 'A');
        this.createChart(this.ctxC, 'C');
        if (!this.isInLowestStep) {
          this.createChart(this.ctxB, 'B');
        }
        if (!this.isInHighestStep) {
          this.createChart(this.ctxA, 'A');
        }
      }
    }
  }

  createChart(ctx: HTMLCanvasElement, type: string) {
    if (type === 'A' && this.chartA) {
      this.chartA.destroy();
    } else if (type === 'B' && this.chartB) {
      this.chartB.destroy();
    } else if (type === 'C' && this.chartC) {
      this.chartC.destroy();
    }

    const middleTextPlugin = (text: string): Plugin => ({
      id: 'middletext',
      beforeDatasetsDraw(chart, args, pluginOptions) {
        const { ctx, data } = chart;
        ctx.save();
        const xCoor = chart.getDatasetMeta(0).data[0].x;
        const yCoor = chart.getDatasetMeta(0).data[0].y;
        ctx.font = 'bold 1rem Plus Jakarta Sans';
        ctx.fillStyle = '#002987';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(text, xCoor, yCoor - 10);
        ctx.fillText('kWh', xCoor, yCoor + 10);
      }
    });

    const dataPercent =
      type === 'A'
        ? this.abovePercent
        : type === 'B'
        ? this.belowPercent
        : this.currentPercent;

    const middleText =
      type === 'B'
        ? `${this.analyzeNettleie.previous_step.limit}-${this.analyzeNettleie.previous_step.next_limit}`
        : type === 'C'
        ? `${this.analyzeNettleie.current_step.limit}-${this.analyzeNettleie.current_step.next_limit}`
        : `${this.analyzeNettleie.next_step.limit}-${this.analyzeNettleie.next_step.next_limit}`;

    const chartColor =
      type === 'A' ? '#002987' : type === 'B' ? '#FFD9C4' : '#0A66FF';

    const config: ChartConfiguration<'doughnut'> = {
      type: 'doughnut',
      data: {
        labels: ['Timer'],
        datasets: [
          {
            data: [Math.ceil(dataPercent), 100 - Math.ceil(dataPercent)],
            label: 'Timer',
            backgroundColor: [chartColor, '#F7F7F7']
          }
        ]
      },
      options: {
        cutout: '75%',
        animation: false,
        plugins: {
          title: {
            display: false
          },
          legend: {
            display: false
          },
          tooltip: {
            enabled: false
          }
        },
        // Disable all events
        events: []
      },
      //@ts-ignore
      plugins: [middleTextPlugin(middleText)]
    };

    if (type === 'A') {
      this.chartA = new Chart<'doughnut'>(ctx, config);
    } else if (type === 'B') {
      this.chartB = new Chart<'doughnut'>(ctx, config);
    } else if (type === 'C') {
      this.chartC = new Chart<'doughnut'>(ctx, config);
    }
  }

  ngOnDestroy() {
    if (this.chartA) {
      this.chartA.destroy();
    }
    if (this.chartB) {
      this.chartB.destroy();
    }
    if (this.chartC) {
      this.chartC.destroy();
    }
  }
}
